import React, { useEffect, useState } from 'react'
import { useTrans } from '@next-nx/hooks'
import { ArrowLeft, ArrowRight, CheckboxMarked, CheckboxUnmarked, Close } from '@next-nx/shared-ui/icons'
import { Button, FormRadio } from '@next-nx/shared-ui/atoms'
import styles from './Filters.module.sass'
import { AttributoType } from '@next-nx/utils/types'

interface HeadProps {
  back?: boolean
  titolo: string
  onBack?: () => void
  onClose: () => void
}

interface ItemProps {
  label: string
  value?: string
  onClick?: () => void
}

interface Props {
  open?: boolean
  setOpen?: (v: boolean) => void
  filters?: AttributoType[]
  selectedFilters?: any
  ordinamenti?: any[]
  selectedOrdinamento?: any
  disabledFilters?: any
  handleFilters: (a: string, b: string, c:number|number[], l?:string, optPk?:number) => void
  handleOrdinamento: (a: string) => void
  totalResults: number
  className?: string
  handleTracking?: (t: string, c: string, e?: string, i?: string) => void
}

const FiltersHead = (props: HeadProps) => {

  const { 
    titolo = '',
    onBack = undefined,
    onClose = () => undefined
  } = props

  return (
    <div className={styles.header}>
      {!!onBack ? (
        <button role="button" className={styles.back} onClick={onBack}>
          <span className={styles.backIcon}><ArrowLeft /></span>
          <span className={styles.backLabel}>{titolo}</span>
        </button>
      ) : (
        <p className={styles.title}>{titolo}</p>
      )}
      <button role="button" className={styles.close} onClick={onClose}>
        <Close />
      </button>
    </div>
  )
}

const FiltersItem = (props: ItemProps) => {
  const {
    label = '',
    value = null,
    onClick = () => undefined
  } = props

  return (
    <button role="button" className={styles.item} onClick={onClick}>
      <div className={styles.itemContent}>
        <span className={styles.itemLabel}>
          {label}
        </span>
        {!!value && (
          <span className={styles.itemValue}>
            {value}
          </span>
        )}
      </div>
      <span className={styles.itemArrow}>
        <ArrowRight />
      </span>
    </button>
  )
}

const Filters = (props: Props) => {

  const { 
    open = false,
    setOpen = () => undefined,
    ordinamenti = [],
    selectedOrdinamento = null,
    filters = [],
    selectedFilters = {},
    disabledFilters = {},
    handleFilters = () => undefined,
    handleOrdinamento = () => undefined,
    totalResults = 0,
    className = '',
    handleTracking = () => undefined
  } = props

  const t = useTrans()
  const [orderOpen, setOrderOpen] = useState(false)
  const [activePanel, setActivePanel] = useState(null)

  useEffect(() => {
    if (open) document.body.classList.add('w-noscrollnav')
    else document.body.classList.remove('w-noscrollnav')
  }, [open])

  // Alla chiusura resettiamo i pannelli aperti
  const onClose = () => {
    setOpen(false)
    setOrderOpen(false)
    setActivePanel(null)
  }

  return (
    <>
      <div className={`${styles.overlay} ${open ? styles['overlay--open'] : ''}`} onClick={() => setOpen(false)} />
      <div className={`${styles.root} ${open ? styles['root--open'] : ''} ${className}`}>
        <FiltersHead titolo={t("Tutti i filtri")} onClose={() => setOpen(false)} />
        <div className={styles.content}>
          {!!ordinamenti.length && (
            <div className={styles.order}>
              <FiltersItem label={t("Ordina per:")} value={selectedOrdinamento?.label} onClick={() => setOrderOpen(true)} />
            </div>
          )}
          {!!filters.length && (
            <div className={styles.list}>
              {filters.filter(x => x.valori_disponibili?.length > 0).map(x => (
                <FiltersItem key={x.pk} value={x.valori_disponibili.filter(o => selectedFilters[x.chiave]?.indexOf(o.value) > -1).map(o => o.nome).join(", ")} label={x.nome} onClick={() => setActivePanel(x.pk)} />
              ))}
            </div>
          )}
        </div>
      </div>
      <div className={`${styles.ctaBox} ${open ? styles['ctaBox--open'] : ''}`}>
        <Button label={t("Cancella tutti")} variant="ghost" onClick={() => {
          handleFilters("remove_all", "", 0)
          }} />
        <Button label={t(totalResults === 1 ? "Vedi 1 risultato" : "Vedi {0} risultati", [totalResults.toString()])} onClick={() => {
          onClose()
          }} />
      </div>
      {!!ordinamenti.length && (
        <div className={`${styles.orderPanel} ${orderOpen ? styles['orderPanel--open'] : ''}`}>
          <FiltersHead titolo={t("Ordina per:")} onClose={onClose} onBack={() => setOrderOpen(false)} />
          <div className={styles.orderPanelList}>
            {ordinamenti.map(opt => (
              <button 
                key={opt.value}
                role="button" 
                onClick={() => {
                  handleOrdinamento(opt.value)
                  setOrderOpen(false)
                }} 
                className={`${styles.panelOption} ${selectedOrdinamento.value === opt.value? styles['panelOption--active'] : ''}`}
              >
                <span className={styles.panelOptionLabel}>{opt.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {!!filters.length && (
        filters.filter(x => x.valori_disponibili?.length > 0).map(x => (
          <div key={x.pk} className={`${styles.valuesPanel} ${activePanel === x.pk ? styles['valuesPanel--open'] : ''}`}>
            <FiltersHead titolo={x.nome} onClose={onClose} onBack={() => setActivePanel(null)} />
            <div className={styles.valuesPanelList}>
              {x.valori_disponibili.map(opt => (
                <button 
                  key={opt.pk}
                  role="button" 
                  onClick={() => {
                    handleFilters("toggle", x.chiave, selectedFilters[x.chiave].indexOf(opt.pk) > -1 ? selectedFilters[x.chiave].filter(y => y !== opt.pk) : selectedFilters[x.chiave].concat([opt.pk]), x.nome, opt.pk)
                  }}
                  className={`${styles.panelOption} ${styles['panelOption--checkbox']} ${selectedFilters?.[x.chiave]?.indexOf(opt.pk) > -1 ? styles['panelOption--active'] : ''}`}
                  disabled={Array.isArray(disabledFilters[x.chiave]) ? disabledFilters[x.chiave]?.indexOf(opt.pk) > -1 : disabledFilters[x.chiave] === opt.pk}
                >
                  <span className={styles.panelOptionIcon}>
                    {selectedFilters?.[x.chiave]?.indexOf(opt.pk) > -1 ? <CheckboxMarked /> : <CheckboxUnmarked />}
                  </span>
                  <span className={styles.panelOptionLabel}>{opt.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))
      )}
    </>
  )
}

export default Filters
